import { useSelector, useDispatch } from 'react-redux';
import { setFormData, setReadOnlyFields } from '../../store/slices/Activation/formSlice';
import { cloneDeep, findIndex, includes, replace } from 'lodash';
import { ERROR_STATUSES, ProfileStatuses, Screens } from '../../constants/Activation/enum';
import { setActivationFailureErrorMsg, setProductUrls, setProfileStatus, setUITheme, setUserPrefLangList } from '../../store/slices/Activation/activationSlice';
import { getActivationDetails } from '../../api/Activation/getActivationDetails';
import { useTranslation } from 'react-i18next';
import { ACTIVATION_CONSTANTS } from '../../constants/Activation/constants';
import { getCSRFToken } from '../../api/csrfToken';
import languageMappings from '../../i18n/languageMappings.json';

/**
 * Hook to call the api and fetch details required for activation and set the state
 * @Hook
 * @param   {function} setScreen  useState function to set the screen to be displayed
 * @param   {function} setLoading useState function to set the loader
 * @return  {function}            getActivationInfo function
 */

const useGetActivationInfo = (setScreen, setLoading) =>
{
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { form } = useSelector((state) => state.form)
  const { uiTheme, productUrls } = useSelector((state) => state.activation)

  const getDisplayNameValue = (value, placeholder) =>
  {
    return value?.trim()?.toUpperCase() === placeholder ? "" : (value || "")
  }

  /**
  * set the UI theme, agreement urls and redirect url
  * @param   {object} uiDetails  UI Details
  * @param   {object} productDetails Product Details.
  */
  const setUIThemeAndUrls = (uiDetails, productDetails) =>
  {
    let uiColourCode = cloneDeep(uiTheme)
    uiColourCode.bannerColour = uiDetails.bannerColor
    uiColourCode.bgColour = uiDetails.backgroundImageColor
    uiColourCode.buttonColour = uiDetails.buttonColor
    uiColourCode.buttonFontColour = uiDetails.buttonFontColor
    dispatch(setUITheme(uiColourCode))

    let productLinks = cloneDeep(productUrls)
    productLinks.logo = uiDetails.logoImageUrl
    productLinks.bgImg = uiDetails.backgroundImageUrl
    productLinks.loginPage = productDetails?.postActivationRedirectUrl

    let termsAndConditions = uiDetails.termsAndConditionsUrl
    let privacyStatement = uiDetails.privacyStatementUrl
    let cookieInformation = uiDetails.cookieInformationUrl
    const lng = navigator.language;
    console.log(lng)

    if (includes(uiDetails.diSupportedLanguages, lng))
    {
      termsAndConditions = replace(termsAndConditions, "{{language}}", lng)
    }
    else
    {
      termsAndConditions = replace(termsAndConditions, "{{language}}", "en")
    }
    if (includes(uiDetails.diSupportedLanguages, lng))
    {
      privacyStatement = replace(privacyStatement, "{{language}}", lng)
    }
    else
    {
      privacyStatement = replace(privacyStatement, "{{language}}", "en")
    }
    if (includes(uiDetails.diSupportedLanguages, lng))
    {
      cookieInformation = replace(cookieInformation, "{{language}}", lng)
    }
    else
    {
      cookieInformation = replace(cookieInformation, "{{language}}", "en")
    }
    productLinks.termsAndConditions = termsAndConditions
    productLinks.privacyStatement = privacyStatement
    productLinks.cookieInformation = cookieInformation

    dispatch(setProductUrls(productLinks))
  }

  /**
   * call getActivationDetails api function and use the response to set the form configuration, UI theme, different urls and screen to be displayed
   * @param   {string} activationToken  Activation Token
   * @param   {string} activationId Activation ID
   * @param   {string} productId Product ID
   */
  const getActivationInfo = async ({ activationToken, activationId, productId }) =>
  {
    try
    {
      setLoading(true)
      const payload = { activationToken, activationId, productId }
      const token = await getCSRFToken("Pre-Activation")
      const response = await getActivationDetails(payload, token.data.csrfToken)
      console.log(response)
      if (response.status === 200)
      {
        setLoading(false)
        const { uiDetails, productDetails } = response.data;

        setUIThemeAndUrls(uiDetails, productDetails)

        if (response.data.description.toUpperCase() === ACTIVATION_CONSTANTS.API_DESCRIPTION.userAlreadyActivated)
        {
          setScreen(Screens.SUCCESS)
        }
        else
        {
          const { userDetails } = response.data;

          let readOnlyFieldsArr = [
            {
              label: "email",
              value: userDetails.email
            }
          ]
          dispatch(setReadOnlyFields(readOnlyFieldsArr))

          let languageNames = new Intl.DisplayNames(["en"], { type: "language" });
          let langList = productDetails?.preferredLanguages?.map((lang) =>
          {
            return {
              value: lang,
              label: languageMappings[lang] || languageNames.of(lang)
            }
          })
          dispatch(setUserPrefLangList(langList))

          dispatch(setProfileStatus(userDetails.profileStatus))
          let formData = cloneDeep(form)
          let firstNameIndex = findIndex(formData, { field: "firstName" })
          let lastNameIndex = findIndex(formData, { field: "lastName" })
          let phoneNumberIndex = findIndex(formData, { field: "phoneNumber" })
          let passwordIndex = findIndex(formData, { field: "password" })
          let languageIndex = findIndex(formData, { field: "language" })

          if (firstNameIndex !== -1)
          {
            formData[firstNameIndex].value = getDisplayNameValue(userDetails.firstName, "FNU")
            formData[firstNameIndex].isReadOnly = false
            formData[firstNameIndex].display = true
            formData[firstNameIndex].mandatory = false
            formData[firstNameIndex].error = false
            formData[firstNameIndex].errorMessage = ""
          }

          if (lastNameIndex !== -1)
          {
            formData[lastNameIndex].value = getDisplayNameValue(userDetails.lastName, "LNU")
            formData[lastNameIndex].isReadOnly = false
            formData[lastNameIndex].display = true
            formData[lastNameIndex].mandatory = false
            formData[lastNameIndex].error = false
            formData[lastNameIndex].errorMessage = ""
          }

          formData[languageIndex].display = productDetails.showPreferredLanguages

          if (userDetails.profileStatus === ProfileStatuses.NOT_COMPLETED && (!productDetails.phoneNumberRequired))
          {
            formData[phoneNumberIndex].mandatory = false
          }
          else if ((userDetails.profileStatus === ProfileStatuses.COMPLETED_WITHOUT_PHONE_NUMBER ||
            userDetails.profileStatus === ProfileStatuses.VERIFICATION_PENDING_WITHOUT_PHONE_NUMBER) &&
            (productDetails.phoneNumberRequired))
          {
            formData[passwordIndex].display = false
            formData[languageIndex].display = false
          }
          else if (userDetails.profileStatus === ProfileStatuses.VERIFICATION_PENDING)
          {
            formData[passwordIndex].display = false
            formData[languageIndex].display = false
            formData[phoneNumberIndex].isReadOnly = true
            formData[phoneNumberIndex].value = userDetails.phoneNumber
            formData[phoneNumberIndex].mandatory = false
            formData[phoneNumberIndex].error = false
            formData[phoneNumberIndex].errorMessage = ""
          }
          else if ((userDetails.profileStatus === ProfileStatuses.VERIFICATION_PENDING_WITHOUT_PHONE_NUMBER && (!productDetails.phoneNumberRequired)))
          {
            formData[passwordIndex].display = false
            formData[languageIndex].display = false
            formData[phoneNumberIndex].mandatory = false
          }
          dispatch(setFormData(formData))

          if (userDetails.profileStatus === ProfileStatuses.COMPLETED ||
            (userDetails.profileStatus === ProfileStatuses.COMPLETED_WITHOUT_PHONE_NUMBER && !productDetails.phoneNumberRequired))
          {
            setScreen(Screens.SUCCESS)
          }
          else
          {
            setScreen(Screens.FORM)
          }
        }
      }
    }
    catch (err)
    {
      console.log(err)
      setLoading(false)
      if (window.location.host === "localhost:3000")
      {
        const apiResponse = {
          "status": "SUCCESS",
          "description": "The requested details have been retrieved successfully.",
          "uiDetails": {
            "bannerColor": "#ffffaf",
            "backgroundImageColor": "#ffffff",
            "buttonColor": "#d04a02",
            "buttonFontColor": "#FFFFFF",
            "logoImageUrl": "http://cdn.mcauto-images-production.sendgrid.net/8fc9f971f3c1fc03/51a8493a-dcef-4a3f-8d2f-54a410b7d23f/568x432.png",
            "termsAndConditionsUrl": "https://terms-auth-dev.pwc.com/terms-of-use-{{language}}.html",
            "privacyStatementUrl": "https://terms-auth-dev.pwc.com/privacy-statement-{{language}}.html",
            "cookieInformationUrl": "https://terms-auth-dev.pwc.com/cookie-information-{{language}}.html",
            "diSupportedLanguages": [
              "en",
              "es",
              "fr",
              "nl",
              "de",
              "da",
              "it",
              "ja",
              "ko",
              "pl",
              "pt",
              "sv",
              "zh-CN",
              "zh-HK",
              "hu"
            ]
          },
          "productDetails": {
            "postActivationRedirectUrl": "http://localhost:5000/myapp.html",
            "showPreferredLanguages": "true",
            "phoneNumberRequired": true,
            "preferredLanguages": [
              "en",
              "es",
              "fr",
              "nl",
              "de",
              "da",
              "it",
              "ja",
              "ko",
              "pl",
              "pt",
              "sv",
              "zh-CN",
              "zh-HK",
              "hu"
            ]
          },
          "userDetails": {
            "email": "selfregistrationy087@mailinator.com",
            "firstName": "FNU",
            "lastName": "LNU",
            "profileStatus": "not-completed",
            "phoneNumber": ""
          }
        }

        const { uiDetails, productDetails, userDetails } = apiResponse;

        setUIThemeAndUrls(uiDetails, productDetails)

        //set read only fields
        let readOnlyFieldsArr = [
          {
            label: "email",
            value: userDetails.email
          }
        ]
        dispatch(setReadOnlyFields(readOnlyFieldsArr))

        //set preferred language list
        let languageNames = new Intl.DisplayNames(["en"], { type: "language" });
        let langList = productDetails?.preferredLanguages?.map((lang) =>
        {
          return {
            value: lang,
            label: languageMappings[lang] || languageNames.of(lang)
          }
        })
        dispatch(setUserPrefLangList(langList))

        //set form fields for different profile status and product type
        dispatch(setProfileStatus(userDetails.profileStatus))
        let formData = cloneDeep(form)
        let firstNameIndex = findIndex(formData, { field: "firstName" })
        let lastNameIndex = findIndex(formData, { field: "lastName" })
        let phoneNumberIndex = findIndex(formData, { field: "phoneNumber" })
        let passwordIndex = findIndex(formData, { field: "password" })
        let languageIndex = findIndex(formData, { field: "language" })

        if (firstNameIndex !== -1)
        {
          formData[firstNameIndex].value = getDisplayNameValue(userDetails.firstName, "FNU")
          formData[firstNameIndex].isReadOnly = false
          formData[firstNameIndex].display = true
          formData[firstNameIndex].mandatory = false
          formData[firstNameIndex].error = false
          formData[firstNameIndex].errorMessage = ""
        }

        if (lastNameIndex !== -1)
        {
          formData[lastNameIndex].value = getDisplayNameValue(userDetails.lastName, "LNU")
          formData[lastNameIndex].isReadOnly = false
          formData[lastNameIndex].display = true
          formData[lastNameIndex].mandatory = false
          formData[lastNameIndex].error = false
          formData[lastNameIndex].errorMessage = ""
        }

        formData[languageIndex].display = productDetails.showPreferredLanguages

        if (userDetails.profileStatus === ProfileStatuses.NOT_COMPLETED && (!productDetails.phoneNumberRequired))
        {
          formData[phoneNumberIndex].mandatory = false
        }
        else if ((userDetails.profileStatus === ProfileStatuses.COMPLETED_WITHOUT_PHONE_NUMBER ||
          userDetails.profileStatus === ProfileStatuses.VERIFICATION_PENDING_WITHOUT_PHONE_NUMBER) &&
          (productDetails.phoneNumberRequired))
        {
          formData[passwordIndex].display = false
          formData[languageIndex].display = false
        }
        else if (userDetails.profileStatus === ProfileStatuses.VERIFICATION_PENDING)
        {
          formData[passwordIndex].display = false
          formData[languageIndex].display = false
          formData[phoneNumberIndex].isReadOnly = true
          formData[phoneNumberIndex].value = userDetails.phoneNumber
          formData[phoneNumberIndex].mandatory = false
          formData[phoneNumberIndex].error = false
          formData[phoneNumberIndex].errorMessage = ""
        }
        else if ((userDetails.profileStatus === ProfileStatuses.VERIFICATION_PENDING_WITHOUT_PHONE_NUMBER && (!productDetails.phoneNumberRequired)))
        {
          formData[passwordIndex].display = false
          formData[languageIndex].display = false
          formData[phoneNumberIndex].mandatory = false
          formData[phoneNumberIndex].error = false
          formData[phoneNumberIndex].errorMessage = ""
        }
        dispatch(setFormData(formData))

        //set screen
        if (userDetails.profileStatus === ProfileStatuses.COMPLETED ||
          (userDetails.profileStatus === ProfileStatuses.COMPLETED_WITHOUT_PHONE_NUMBER && !productDetails.phoneNumberRequired))
        {
          setScreen(Screens.SUCCESS)
        }
        else
        {
          setScreen(Screens.FORM)
        }
      }
      else if (err.response.data?.error?.status === ERROR_STATUSES.INVALID_INPUT)
      {
        const { uiDetails } = err.response.data.error;

        setUIThemeAndUrls(uiDetails)

        if (includes(err.response.data.error.description, "expired"))
        {
          dispatch(setActivationFailureErrorMsg(t('activationErrorScreen.expiredInvitation')))
        }
        else
        {
          dispatch(setActivationFailureErrorMsg(t('activationErrorScreen.invalidInvitation')))
        }
        setScreen(Screens.ACTIVATION_FAILURE)
      }
      else
      {
        setScreen(Screens.FALLBACK_ERROR)
      }
    }
  }
  return [getActivationInfo]
}
export default useGetActivationInfo;